import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuthentication } from 'hooks';
import { Loader } from 'components';
import { authenticationService } from 'services';

const OPAQUE_CODE_AUTH = process.env.REACT_APP_OPAQUE_CODE_AUTH === 'true';

const isMobileOrSafari = () => {
  const ua = navigator.userAgent || '';
  const safari = /^((?!chrome|android).)*safari/i.test(ua);
  const mobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua.toLowerCase());
  return safari || mobile;
};

export function UnprotectedPages() {
    const { user, loading } = useAuthentication();
    const location = useLocation();
    const [sessionReady, setSessionReady] = useState(false);
    const [sessionSyncing, setSessionSyncing] = useState(false);

    const urlParams = new URLSearchParams(location.search);
    const returnUrl = location.state?.returnUrl || urlParams.get('returnUrl');
    const hasReturnUrl = !!(user && returnUrl);

    useEffect(() => {
        if (hasReturnUrl && !sessionReady && !sessionSyncing) {
            setSessionSyncing(true);

            const waitForSession = async () => {
                // If signInWithEmail is still in flight, wait for it to
                // complete before we try to read or generate _pendingCode.
                // Poll every 50ms for up to 3 seconds.
                if (authenticationService._signingIn) {
                    let waited = 0;
                    while (authenticationService._signingIn && waited < 3000) {
                        await new Promise((r) => setTimeout(r, 50));
                        waited += 50;
                    }
                }

                try {
                    if (OPAQUE_CODE_AUTH && isMobileOrSafari()) {
                        // signIn() in the provider may have already called
                        // createServerSession and stored _pendingCode before
                        // redirecting. If it did, it will have handled the
                        // redirect itself — we should never reach here with a
                        // pending code still set. But if we do (e.g. the user
                        // was already signed in and landed on one-auth-ui
                        // directly with a returnUrl), generate the code now.
                        let code = authenticationService._pendingCode;

                        if (!code) {
                            // No code from signIn() — need to generate one.
                            // forRedirect: true forces createServerSession even
                            // on Safari so _pendingCode gets populated.
                            await authenticationService.syncServerSession(user, { forRedirect: true });
                            code = authenticationService._pendingCode;
                        }

                        if (code) {
                            authenticationService._pendingCode = null;
                            const url = new URL(returnUrl);
                            url.searchParams.set('code', code);
                            // Small delay to ensure the fetch response has fully
                            // committed before Safari navigates away.
                            await new Promise((r) => setTimeout(r, 150));
                            window.location.href = url.toString();
                            return;
                        }

                        // No code at all — createServerSession failed.
                        // Fall through and redirect without code; destination
                        // app will send user back to sign-in.
                        console.warn('[auth] No code available for redirect — redirecting without code');
                    } else {
                        await authenticationService.syncServerSession(user, { forRedirect: true });
                    }

                    setSessionReady(true);
                } catch (error) {
                    console.error('Session sync failed:', error);
                    // Proceed anyway to avoid infinite loading
                    setSessionReady(true);
                } finally {
                    setSessionSyncing(false);
                }
            };

            waitForSession();
        }
    }, [user, hasReturnUrl, sessionReady, sessionSyncing]);

    if (loading) {
        return <Loader loadingMessage="Loading..." />;
    }

    if (user) {
        if (returnUrl) {
            if (!sessionReady) {
                return <Loader loadingMessage="Completing sign-in..." />;
            }

            localStorage.removeItem('auth_return_url');

            if (returnUrl.startsWith('http')) {
                window.location.href = returnUrl;
                return <Loader loadingMessage="Redirecting..." />;
            }
            return <Navigate to={returnUrl} replace />;
        }

        return <Navigate to="/profile" replace />;
    }

    // Handle root redirect for unauthenticated users
    if (location.pathname === '/') {
        return <Navigate to="/sign-in" replace />;
    }

    return <Outlet />;
}